import * as React from 'react';

import { Interactive, PopoverPortalWrapper, PortalToggleable } from '@gitbook/react-uikit';
import type { CursorDOMAttributes, SelectionProps } from '@gitbook/slate-react';

import { getRealTimeColor } from '../../Users';
import type { RealtimeRangeData } from './types';

const CARRET_WIDTH = 2;
const BUBBLE_MAX_WIDTH = 250;

/**
 * Highlight a participant's focus (cursor).
 */
export function RealtimeSelectionFocus(props: SelectionProps): React.ReactElement | null {
    const { selection } = props;

    const [bubbleVisible, setBubbleVisible] = React.useState(false);

    const attributes: CursorDOMAttributes = {
        'data-cursor': true,
    };

    const selectionData = selection.data.toFrozenObject<RealtimeRangeData>();
    const color = getRealTimeColor(selectionData.userId);
    const userStyle: React.CSSProperties = {
        backgroundColor: color.base,
    };

    // Don't render realtime selections in tests as it can be unpredictable
    if (process.env.GITBOOK_PREDICTABLE_UI) {
        return null;
    }

    return (
        <div {...attributes} className="absolute inline-flex">
            <PortalToggleable
                wrapper={PopoverPortalWrapper}
                onOpen={() => setBubbleVisible(true)}
                onClose={() => setBubbleVisible(false)}
                anchor={({ ref, open, close }) => (
                    <Interactive.WithInteractiveState onHoverIn={open} onHoverOut={close}>
                        {({ events }) => (
                            <div
                                ref={ref}
                                {...events}
                                className="relative select-none"
                                style={{
                                    ...userStyle,
                                    top: 3,
                                    height: '1.2em',
                                    width: `${CARRET_WIDTH}px`,
                                }}
                            >
                                <div
                                    className="absolute rounded-full"
                                    style={{
                                        ...userStyle,
                                        left: `-${CARRET_WIDTH}px`,
                                        top: `-${CARRET_WIDTH * 3}px`,
                                        width: `${CARRET_WIDTH * 3}px`,
                                        height: `${CARRET_WIDTH * 3}px`,
                                    }}
                                />
                            </div>
                        )}
                    </Interactive.WithInteractiveState>
                )}
            >
                {bubbleVisible && (
                    <div
                        className="fade-in absolute animate-in"
                        style={{
                            left: `-${CARRET_WIDTH}px`,
                            width: `${BUBBLE_MAX_WIDTH}px`,
                            top: '-1.3em',
                            ...userStyle,
                        }}
                    >
                        <div className="absolute rounded px-3 py-0.5" style={userStyle}>
                            <div className="text-[--sp-color-text-realtime-bubble] text-ui-small">
                                {selectionData.userDisplayName}
                            </div>
                        </div>
                    </div>
                )}
            </PortalToggleable>
        </div>
    );
}
